// Renders the legacy PL Hub policy wording for the employee forms. The text
// lives in translation.json under employeeForms (copied word for word from
// the legacy Views/Forms/*.cshtml, English and Spanish), so employees sign
// the company's actual policy rather than a summary.
import React from 'react';
import { Box, Typography } from '@mui/material';
import i18n from '../../../i18n';

const BLANK = '__________';

// TKT-575: keep browsers and password managers from filling the name boxes
// on the employee forms with a saved login or another person's name
// (Naomi's Company Policy form was saved with Employee Name "carlos"). The
// box is still pre-filled with the employee's own name and stays editable.
export const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};

// Forms pass language 'en' or 'es'; the locale files are en and es-MX.
export const getFormT = (language) => i18n.getFixedT(language === 'es' ? 'es-MX' : 'en');

export const getFormText = (language, key) => getFormT(language)(`employeeForms.${key}`, { returnObjects: true });

// "**bold**" runs plus [[name]] / [[serial]] style placeholders.
export const RichText = ({ text, values = {} }) => {
  const filled = String(text || '').replace(/\[\[(\w+)\]\]/g, (m, key) => (values[key] || '').trim() || BLANK);
  return filled.split('**').map((part, i) => (
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
  ));
};

const PolicyText = ({ language = 'en', policyKey, values }) => {
  const policy = getFormText(language, `policies.${policyKey}`);
  const blocks = Array.isArray(policy?.blocks) ? policy.blocks : [];

  return (
    <Box>
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`;
        if (block.type === 'heading') {
          return (
            <Typography key={key} variant="subtitle1" sx={{ fontWeight: 600, mt: index === 0 ? 0 : 2.5, mb: 1 }}>
              <RichText text={block.text} values={values} />
            </Typography>
          );
        }
        if (block.type === 'subheading') {
          return (
            <Typography key={key} variant="subtitle2" sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}>
              <RichText text={block.text} values={values} />
            </Typography>
          );
        }
        if (block.type === 'list') {
          return (
            <Box key={key} component="ul" sx={{ mt: 0, mb: 1.5, pl: 3 }}>
              {block.items.map((item, i) => (
                <Typography key={i} component="li" variant="body2" sx={{ mb: 0.75 }}>
                  <RichText text={item} values={values} />
                </Typography>
              ))}
            </Box>
          );
        }
        return (
          <Typography key={key} variant="body2" sx={{ mb: 1.5 }}>
            <RichText text={block.text} values={values} />
          </Typography>
        );
      })}
    </Box>
  );
};

export default PolicyText;
